<template>
  <Transition name="modal-fade">
    <div v-if="useError.show.value" class="error-modal-overlay">
      <div class="error-modal" role="alertdialog" aria-modal="true" aria-labelledby="error-title">
        <div class="error-modal__header">
          <div class="error-modal__icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
              <path fill="none" d="M0 0h24v24H0z"/>
              <path 
                d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-7v2h2v-2h-2zm0-8v6h2V7h-2z"
                fill="currentColor"
              />
            </svg>
          </div>
          <h3 id="error-title" class="error-modal__title">Alert</h3>
        </div>
        <div class="error-modal__body">
          <p class="error-modal__message">{{ useError.errorMessage.value }}</p>
        </div>
        <div class="error-modal__footer">
          <button class="error-modal__button" @click="closePopup" aria-label="Close error message">
            OK
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { watch, onUnmounted, onMounted } from 'vue';
import { useErrorPopup } from '~/composables/useErrorPopup';

const { useError } = useErrorPopup();

let timer: number | null = null;

// Watch for changes in the show prop to set up auto-close timer
watch(() => useError.show.value, (newVal) => {
  if (newVal) {
    // When modal shows, add a class to prevent body scrolling
    if (typeof document !== 'undefined') {
      document.body.classList.add('modal-open');
    }
  } else {
    // When modal hides, remove the class to allow body scrolling
    if (typeof document !== 'undefined') {
      document.body.classList.remove('modal-open');
    }
    
    // Clear timer if it exists
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }
}, { immediate: true });

// Close the popup
const closePopup = () => {
  useError.show.value = false;
  
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
};

// Add keyboard event listener for Escape key
const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && useError.show.value) {
    closePopup();
  }
};

// Set up keyboard event listeners
onMounted(() => {
  window.addEventListener('keydown', handleKeydown);
});

// Clean up on component unmount
onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
  
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  
  // Ensure body scrolling is re-enabled
  if (typeof document !== 'undefined') {
    document.body.classList.remove('modal-open');
  }
});
</script>

<style scoped>
.error-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.error-modal {
  width: 90%;
  max-width: 420px;
  background-color: var(--color-surface);
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: modal-appear 0.25s ease-out;
}

.error-modal__header {
  background-color: var(--color-primary-dark);
  color: var(--color-white);
  padding: 16px 20px;
  display: flex;
  align-items: center;
}

.error-modal__icon {
  margin-right: 12px;
  color: var(--color-white);
  display: flex;
  align-items: center;
}

.error-modal__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-gray-lightest);
}

.error-modal__body {
  padding: 20px;
  border-bottom: 1px solid var(--color-gray-lightest);
}

.error-modal__message {
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
  color: var(--color-text-primary);
}

.error-modal__footer {
  padding: 16px 20px;
  display: flex;
  justify-content: flex-end;
  background-color: var(--color-background-light);
}

.error-modal__button {
  background-color: var(--color-primary-dark);
  color: var(--color-white);
  border: none;
  border-radius: 6px;
  padding: 8px 24px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.error-modal__button:hover {
  background-color: var(--color-primary-dark);
}

.error-modal__button:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 121, 107, 0.4);
}

/* Transition animations */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@keyframes modal-appear {
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@media (max-width: 640px) {
  .error-modal {
    width: 95%;
    max-width: none;
    margin: 0 10px;
  }
}

@media (max-width: 640px) {
  .error-popup {
    left: 20px;
    right: 20px;
    max-width: calc(100% - 40px);
  }
}
</style>
